import {
  UserManager,
  WebStorageStateStore,
  type User,
  type UserManagerSettings,
} from 'oidc-client-ts';
import type { RuntimeConfig } from './config';

/**
 * OIDC client singleton.
 *
 * We wrap oidc-client-ts's UserManager with a thin API surface:
 *
 *   - login()       → redirect to Keycloak's authorize endpoint
 *   - callback()    → swap the code for tokens on /callback
 *   - getUser()     → returns the cached User or null
 *   - logout()      → RP-initiated logout, redirects back to /
 *   - authHeader()  → `Bearer <jwt>` or null (for the API client)
 *
 * Configuration:
 *   - authority   = issuer URL (Keycloak 25 supports discovery under
 *                   `${issuer}/.well-known/openid-configuration`)
 *   - client_id   = public OIDC client on the atlas realm
 *   - response_type = 'code' (PKCE)
 *   - scope       = 'openid profile email'
 *   - loadUserInfo = false — we don't need the userinfo endpoint,
 *                    the ID token + access token cover everything.
 *
 * We persist the user in localStorage so a page refresh doesn't kick
 * the user back to the login redirect. oidc-client-ts handles silent
 * renewal via the refresh token when `automaticSilentRenew` is on.
 */
let manager: UserManager | null = null;

export function initAuth(cfg: RuntimeConfig): UserManager {
  if (manager) return manager;

  const settings: UserManagerSettings = {
    authority: cfg.oidcIssuer,
    client_id: cfg.oidcClientId,
    redirect_uri: cfg.oidcRedirectUri,
    post_logout_redirect_uri: cfg.oidcPostLogoutRedirectUri,
    response_type: 'code',
    scope: 'openid profile email',
    loadUserInfo: false,
    // Silent renewal keeps the access token fresh without bouncing the
    // user back through Keycloak. oidc-client-ts will call the refresh
    // token endpoint in the background ~1 minute before the access
    // token expires.
    automaticSilentRenew: true,
    // Persist in localStorage so reloads survive. sessionStorage would
    // evict on tab close, which is annoying in dev.
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // Keycloak 25 puts /protocol/openid-connect/logout behind `end_session_endpoint`
    // in discovery, so we don't need to override metadata manually.
  };

  manager = new UserManager(settings);
  return manager;
}

export function getManager(): UserManager {
  if (!manager) {
    throw new Error('auth not initialised — call initAuth(cfg) first');
  }
  return manager;
}

export async function getUser(): Promise<User | null> {
  return getManager().getUser();
}

export async function login(): Promise<void> {
  await getManager().signinRedirect();
}

export async function handleCallback(): Promise<User> {
  return getManager().signinRedirectCallback();
}

export async function logout(): Promise<void> {
  await getManager().signoutRedirect();
}

export async function authHeader(): Promise<string | null> {
  const user = await getUser();
  if (!user || user.expired) return null;
  return `Bearer ${user.access_token}`;
}
